import {DOM} from "../../../../../public/js/utils/DOM";
import {RecursivePartial, TomSettings} from "tom-select/dist/types/types";
import {SelectInputCustomDataItem} from "./definitions/SelectInput";
import {StringValidator} from "../../../../../public/js/utils/Validator/StringValidator";
import {SelectInputCreateTypeEnum} from "./definitions/SelectInputCreateTypeEnum";
import {Modal} from "../../../../../public/js/utils/Modal";
import {StringValidatorValidationType} from "../../../../../public/js/definitions/utils/validator/stringValidator/StringValidatorValidationType";
import {Price} from "../../../../../public/js/utils/Price";
import {SalesPriceDataValue} from "../../../Sales/definitions/SalesPriceDataValue";
const TomSelect = require('tom-select');

export namespace ControlsFormSelectInput {

    export class Config {
        public static readonly inputContainerClass = "SelectInput";
        public static readonly inputClass = "SelectInput-input";
        public static readonly searchInputContainerClassName = "dropdown-input-wrap";
        public static readonly searchInputElementClassName = "dropdown-input";
        public static readonly translationElementId = "jsTranslations";
        public static readonly customDataDataName = "customData";
        public static readonly createEnabledDataName = "allowCreate";
        public static readonly createEnabledTypeDataName = "allowCreateType";
        public static readonly customSearchTextDataName = "searchText";
        public static readonly customNoResultsTextDataName = "noResultsText";
        public static readonly customAddTextDataName = "addText";
        public static readonly changeModeDataName = "changeMode";
        public static readonly disableSearchOptionDataName = "disableSearch";
        public static readonly stringLabelDataName = "label";
        public static readonly inputControlElementClassName = "ts-control";
        public static readonly inputControlItemElementClassName = "item";
        public static readonly inputControlItemPriceLabelElementClassName = "priceLabel";
        public static readonly selectInputSelectedQuery = "option"
    }

    export class Control {

        /**
         * Main initialization function
         * @param container - form container element
         */
        public static initialize(container?: HTMLElement) : void {

            const inputs = DOM.Parser.getHtmlElements(Config.inputContainerClass, container, true) as HTMLCollectionOf<HTMLDivElement>;
            for (let i = 0; i < inputs.length; i++) {
                this.initializeElement(inputs[i]);
            }
        }

        /**
         * When search is disabled, we use first letter to select best option
         * Get and find the best option and set it active
         * @param startIndex - start option index to look in
         * @param options - list of options
         * @param eventKey - key (first letter) from event
         * @param instance - tom select instance
         * @private
         */
        private static updateActiveOption(startIndex: number, options: HTMLCollectionOf<HTMLElement>, eventKey: string, instance: any) : boolean {

            for (let i = startIndex; i < options.length; i++) {
                const optionValue = DOM.Parser.getDataByNameFromElementOrNull('value', options[i]);
                const optionValueFirstChar = optionValue === null ? null : optionValue.charAt(0);
                if (eventKey === optionValueFirstChar && !options[i].classList.contains('active')) {
                    instance.setActiveOption(options[i]);
                    return true;
                }
            }

            return false;
        }

        /**
         * Initialize concrete element
         * @param element - input container main element
         * @todo timeout remove
         * @private
         */
        private static initializeElement(element: HTMLDivElement) : void {

            // Load main info props from HTML data
            const input = DOM.Parser.getHtmlElementByClass(Config.inputClass, element) as HTMLInputElement;
            const translations = DOM.Parser.getHtmlElement(Config.translationElementId);
            const customNoResultsText = DOM.Parser.getDataByNameFromElementOrNull(Config.customNoResultsTextDataName, input);
            const customAddText = DOM.Parser.getDataByNameFromElementOrNull(Config.customAddTextDataName, input);
            const disableSearch = DOM.Parser.getDataByNameFromElementOrNull(Config.disableSearchOptionDataName, input) === 'yes';
            const stringLabel = DOM.Parser.getDataByNameFromElementOrNull(Config.stringLabelDataName, input);
            const changeMode = DOM.Parser.getDataByNameFromElementOrNull(Config.changeModeDataName, input) === 'yes';
            const isMultiSelectInput = input.classList.contains('MultiSelectInput');

            // Configure LIB
            let options: RecursivePartial<TomSettings> = {
                // @ts-ignore
                controlInput: null,
                render: {
                    no_results: function(){
                        return `
                            <div class="no-results">
                                ${customNoResultsText ?? DOM.Parser.getDataByNameFromElement('trsSelectNoResults', translations)}
                            </div>`;
                    },
                    option_create: function(data: any, escape: any){
                        return `
                            <div class="create">
                                ${customAddText ??DOM.Parser.getDataByNameFromElement('trsSelectAdd', translations)} 
                                <strong>${escape(data.input)}</strong>&hellip;
                            </div>`;
                    },
                },
                plugins: isMultiSelectInput ? {
                    'dropdown_input': true,
                    'checkbox_options': {
                        'checkedClassNames':   ['ts-checked'],
                        'uncheckedClassNames': ['ts-unchecked'],
                    }
                } : {
                    'dropdown_input': true,
                },
                onDropdownOpen: (dropdown: HTMLDivElement) => {
                    if (Modal.Helpers.getShownModal() !== null) {
                        dropdown.scrollIntoView({block: "end", behavior: "instant"});
                    }
                },
                onInitialize: () => {
                    if (!isMultiSelectInput) {
                        setTimeout(() => {
                            Control.extPriceSelected(element, input, changeMode);
                        }, 500)
                    }
                }
            };

            // Process extensions and init instance
            options = this.extCustomData(options, input, changeMode);
            options = this.extCreate(options, input);
            const instance = new TomSelect(input, options);

            // When search is disabled, we can select first item by first char
            if (disableSearch) {
                DOM.Parser.getHtmlElementByClass(Config.searchInputContainerClassName, element).style.display = 'none';
                element.addEventListener('keyup', (event: KeyboardEvent) => {
                    if (event.key.length !== 1) { return; }
                    const options = DOM.Parser.getHtmlElements('option', element);
                    const activeOptionIndex = instance.activeOption === null ? 0 : parseInt(instance.activeOption.id.split('-').slice(-1));
                    let foundInNextOptions = this.updateActiveOption(activeOptionIndex, options, event.key, instance);
                    if (!foundInNextOptions) { this.updateActiveOption(0, options, event.key, instance); }
                })
            }

            // Update search view
            else {
                const searchIcon = document.createElement("img");
                searchIcon.src = '/resources/images/2023/search.svg';
                DOM.Parser.getHtmlElementByClass(Config.searchInputContainerClassName, element).prepend(searchIcon);
                const customSearchText = DOM.Parser.getDataByNameFromElementOrNull(Config.customSearchTextDataName, input);
                (DOM.Parser.getHtmlElementByClass(Config.searchInputElementClassName, element) as HTMLInputElement).placeholder
                    = customSearchText ?? DOM.Parser.getDataByNameFromElement('trsSelectSearch', translations);
            }

            // Update label
            if (stringLabel) {
                DOM.Parser.getHtmlElementByQuery('label',
                    DOM.Parser.getParentElement(
                        DOM.Parser.getParentElement(input))).classList.add('active');
            }
        }

        /**
         * Extension to work with prices in custom data
         * @param element - main element container
         * @param input - input element
         * @param changeMode - change mode enabled (show +/-)
         * @private
         */
        private static extPriceSelected(element: HTMLElement, input: HTMLInputElement, changeMode: boolean): void {

            // Trigger input change
            input.addEventListener('change', () => {
                this.extPriceSelected(element, input, changeMode);
            });

            // If option not found or prices not available skip
            const foundOption = this.findOptionInCustomData(input.value, input);
            if (!foundOption || !foundOption.price) { return; }

            // Get current price label element
            const controlElement = DOM.Parser.getHtmlElementByClass(Config.inputControlElementClassName, element);
            const itemElement = DOM.Parser.getHtmlElementByClass(Config.inputControlItemElementClassName, controlElement);
            const priceLabel = controlElement.getElementsByClassName(Config.inputControlItemPriceLabelElementClassName) as HTMLCollectionOf<HTMLDivElement>;
            let priceLabelItem = priceLabel.length === 0 ? null : priceLabel[0];

            // If not created then create one
            if (!priceLabelItem) {
                priceLabelItem = document.createElement("div");
                priceLabelItem.classList.add('priceLabel');
                itemElement.append(priceLabelItem);
            }

            // Update price label content
            const priceString = (new Price.Initializer()).getPriceAmountStringFromPriceObject(foundOption.price, false, changeMode);
            priceLabelItem.innerHTML = priceString.length === 0 ? "&nbsp" : priceString;
        }

        /**
         * Create new item extended functionality
         * @param options - current LIB options
         * @param input - select input
         * @private
         */
        private static extCreate(options: RecursivePartial<TomSettings>, input: HTMLInputElement) : RecursivePartial<TomSettings> {

            const createEnabled = DOM.Parser.getDataByNameFromElement(Config.createEnabledDataName, input) === 'yes';
            options.create = createEnabled;
            if (!createEnabled) { return options; }

            const createType = DOM.Parser.getDataByNameFromElement(Config.createEnabledTypeDataName, input) as SelectInputCreateTypeEnum;
            if (createType === SelectInputCreateTypeEnum.EMAIL) {
                // @ts-ignore
                options.create = function(input: string) {
                    if (StringValidator.validateString(input, {meta: null, name: StringValidatorValidationType.EMAIL})) {
                        return {value:input,text:input};
                    }
                    return false;
                };
                options.createFilter = function(input: string) {
                    return StringValidator.validateString(input, {meta: null, name: StringValidatorValidationType.EMAIL});
                }
            }

            return options;
        }

        /**
         * Extension to handle custom data for options
         * @param options - LIB options
         * @param input - select input element
         * @param changeMode
         * @private
         */
        private static extCustomData(options: RecursivePartial<TomSettings>, input: HTMLInputElement, changeMode: boolean) : RecursivePartial<TomSettings> {

            const customData = DOM.Parser.getDataByNameFromElementOrNull(Config.customDataDataName, input);
            if (customData === null) { return  options; }

            options.render!.option =  function(data: {value: string|number, text: string}, escape: any) {

                const foundOption = Control.findOptionInCustomData(data.value.toString(), input);
                const descriptionText = foundOption
                    ? foundOption.price
                        ? (new Price.Initializer()).getPriceAmountStringFromPriceObject(foundOption.price, false, changeMode)
                        : foundOption.desc
                    : null;


                if (!descriptionText) {
                    return `<div data-value="${escape(data.value)}" role="option">${escape(data.text)}</div>`;
                }

                return `
                        <div data-value="${escape(data.value)}" role="option">
                            <div class="option-text">
                                <div class="option-text-title">${escape(data.text)}</div>
                                <div class="option-text-description">${escape(descriptionText)}</div>
                            </div>
                        </div>
                    `;
            };

            return options;
        }

        /**
         * Find option with id in custom data and return it
         * @param identify - option identification (value)
         * @param input - input element
         * @private
         */
        private static findOptionInCustomData(identify: string, input: HTMLInputElement) : SelectInputCustomDataItem|null {

            const customData = DOM.Parser.getDataByNameFromElementOrNull(Config.customDataDataName, input);
            if (!customData) { return null; }
            const customDataDecoded : Array<SelectInputCustomDataItem> = JSON.parse(customData);
            return customDataDecoded.find((item: SelectInputCustomDataItem) => {
                return item.value.toString() === identify;
            }) ?? null;
        }

        /**
         * Get price list from select input
         * @param input - select input
         */
        public static getSelectInputPriceList(input: HTMLElement) : SalesPriceDataValue[] {

            // Get custom data with price
            const prices: SalesPriceDataValue[] = [];
            const selectElement = DOM.Parser.getHtmlElementByClass(Config.inputClass, input);
            const helperData = JSON.parse(DOM.Parser.getDataByNameFromElement(Config.customDataDataName, selectElement)) as Array<SelectInputCustomDataItem>;

            // Process through options and use selected, then calculate price from custom data
            const options = DOM.Parser.getHtmlElementsByQuery(Config.selectInputSelectedQuery, selectElement) as NodeListOf<HTMLOptionElement>;
            for (let j = 0; j < options.length; j++) {
                const option = options[j];
                if (!option.selected) { continue; }
                const price = this.getPriceFromSelectInputHelperData(option.value, helperData);
                if (price) { prices.push(price); }
            }

            return prices;
        }

        /**
         * Used in SelectInput
         * Helper function to find price in custom data
         * @param value - selected option value
         * @param data - custom data parsed
         * @private
         */
        private static getPriceFromSelectInputHelperData(value: string, data: Array<SelectInputCustomDataItem>) : SalesPriceDataValue|null {

            for (const dataItem of data) {
                if (dataItem.value.toString() === value && dataItem.price) {
                    return dataItem.price;
                }
            }

            return null;
        }
    }
}